import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { FormInstance } from "antd";

/**
 * Re-translates validation errors that are showing when the language changes.
 *
 * Ant Design stores each rule `message` node when validation runs, so a `t(...)` inside it keeps
 * the language it was evaluated in. Re-validating only the fields that currently have errors
 * rebuilds those messages. Error-free fields are left alone so switching language never makes
 * new errors appear.
 */
export function useRevalidateOnLanguageChange(form: FormInstance) {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const previousLanguage = useRef(language);

  useEffect(() => {
    if (previousLanguage.current === language) {
      return;
    }

    previousLanguage.current = language;

    const fieldsWithErrors = form
      .getFieldsError()
      .filter(({ errors }) => errors.length > 0)
      .map(({ name }) => name);

    if (fieldsWithErrors.length === 0) {
      return;
    }

    // Validation failing is expected here, the errors are what we are re-translating.
    form.validateFields(fieldsWithErrors).catch(() => undefined);
  }, [form, language]);
}
